import  { useState, useRef } from 'react';
import {
    Box,
    Container,
    Typography,
    Tabs,
    Tab,
    Grid,
    Card,
    CardContent,
    alpha,
    useMediaQuery,
    useTheme,
    styled,
} from '@mui/material';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { P } from '../theme/palette';
import { MODULES } from '../data/module_system_data';
import { useTenantConfig } from '../hooks/useTenant';


const fadeUp = {
    hidden: { opacity: 0, y: 30 },
    visible: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
    },
};

const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: (i) => ({
        opacity: 1,
        y: 0,
        transition: {
            delay: i * 0.08,
            duration: 0.5,
            ease: [0.22, 1, 0.36, 1],
        },
    }),
};


// Section badge pill component - Kurumsal
const SectionBadge = styled(Box)({
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    padding: '8px 16px',
    borderRadius: 50,
    background: 'rgba(30, 64, 175, 0.06)',
    border: '1px solid rgba(30, 64, 175, 0.12)',
    marginBottom: 20,
});

const ModuleSystemSectionKurumsal = () => {
    const { colors , tenantName } = useTenantConfig();
    const [activeTab, setActiveTab] = useState('bagis-finans');
    const [hoveredCard, setHoveredCard] = useState(null);
    const ref = useRef(null);
    const isInView = useInView(ref, { once: true, margin: '-100px' });
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    const activeModule = MODULES[activeTab];
    const tabKeys = Object.keys(MODULES);

    return (
        <Box
            ref={ref}
            sx={{
                py: { xs: 10, md: 14 },
                background: 'linear-gradient(165deg, rgba(239, 246, 255, 0.5) 0%, #f8fafc 50%, #ffffff 100%)',
                borderTop: '1px solid #e5e7eb',
                position: 'relative',
                overflow: 'hidden',
            }}
        >
            {/* Dekoratif arka plan */}
            <Box
                sx={{
                    position: 'absolute',
                    top: '-10%',
                    left: '-5%',
                    width: 600,
                    height: 600,
                    background: 'radial-gradient(circle, rgba(30, 64, 175, 0.04) 0%, transparent 70%)',
                    borderRadius: '50%',
                    filter: 'blur(100px)',
                    pointerEvents: 'none',
                }}
            />

            <Container maxWidth="lg">
                {/* ===== HEADER ===== */}
                <motion.div
                    variants={fadeUp}
                    initial="hidden"
                    animate={isInView ? 'visible' : 'hidden'}
                >
                    <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 8 } }}>
                        <motion.div
                            variants={fadeUp}
                            initial="hidden"
                            animate={isInView ? 'visible' : 'hidden'}
                        >
                            <SectionBadge>
                                <Box
                                    sx={{
                                        width: 8,
                                        height: 8,
                                        borderRadius: '50%',
                                        background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)`,
                                    }}
                                />
                                <Typography
                                    sx={{
                                        fontSize: '0.8rem',
                                        fontWeight: 600,
                                        background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)`,
                                        WebkitBackgroundClip: 'text',
                                        WebkitTextFillColor: 'transparent',
                                        letterSpacing: '0.5px',
                                    }}
                                >
                                    MODÜL SİSTEMİ
                                </Typography>
                            </SectionBadge>
                        </motion.div>

                        <Typography
                            variant="h2"
                            sx={{
                                fontWeight: 900,
                                fontSize: { xs: '2.4rem', md: '3rem' },
                                color: '#0f172a',
                                mb: 2,
                                letterSpacing: '-0.02em',
                                lineHeight: 1.15,
                            }}
                        >
                            İhtiyacınıza Göre Şekillenen
                            <br />
                            Modül Sistemi
                        </Typography>

                        <Typography
                            variant="h6"
                            sx={{
                                maxWidth: 760,
                                mx: 'auto',
                                fontSize: { xs: '1.05rem', md: '1.15rem' },
                                color: '#64748b',
                                lineHeight: 1.7,
                                fontWeight: 500,
                            }}
                        >
                            {tenantName}, derneğinizin ihtiyacına göre şekillenen
                            modüler bir yapıya sahiptir. İhtiyacınız olan modülleri
                            aktif edin ve sisteminizi kolayca büyütün.
                        </Typography>
                    </Box>
                </motion.div>

                {/* ===== TABS ===== */}
                <Box
                    sx={{
                        mb: 6,
                        borderBottom: `2px solid ${alpha('#000', 0.06)}`,
                        overflowX: 'auto',
                        '&::-webkit-scrollbar': {
                            height: 6,
                        },
                        '&::-webkit-scrollbar-track': {
                            background: alpha('#000', 0.03),
                            borderRadius: 10,
                        },
                        '&::-webkit-scrollbar-thumb': {
                            background: alpha(P.teal[500], 0.3),
                            borderRadius: 10,
                            '&:hover': {
                                background: alpha(P.teal[500], 0.5),
                            },
                        },
                    }}
                >
                    <Tabs
                        value={activeTab}
                        onChange={handleTabChange}
                        variant={isMobile ? 'scrollable' : 'fullWidth'}
                        scrollButtons={isMobile ? 'auto' : false}
                        sx={{
                            minHeight: 56,
                            '& .MuiTabs-indicator': {
                                height: 3,
                                background: activeModule.color,
                                borderRadius: '3px 3px 0 0',
                            },
                            '& .MuiTab-root': {
                                minHeight: 56,
                                textTransform: 'none',
                                fontWeight: 600,
                                fontSize: { xs: '0.85rem', md: '0.95rem' },
                                color: '#94a3b8',
                                transition: 'all 0.3s ease',
                                px: { xs: 2, md: 3 },
                                background: 'transparent',
                                '&:hover': {
                                    color: activeModule.color,
                                    background: alpha(activeModule.color, 0.05),
                                },
                                '&.Mui-selected': {
                                    color: activeModule.color,
                                    fontWeight: 700,
                                    background: alpha(activeModule.color, 0.08),
                                },
                            },
                        }}
                    >
                        {tabKeys.map((key) => (
                            <Tab
                                key={key}
                                label={MODULES[key].title}
                                value={key}
                                // icon={
                                //     <Box
                                //         component={'img'}
                                //         src={MODULES[key].image}
                                //         sx={{
                                //             width: 30,
                                //             height: 30,
                                //             color: activeTab === key ? MODULES[key].color : 'inherit',
                                //             transition: 'color 0.3s ease',
                                //         }}
                                //     />
                                // }
                                iconPosition="start"
                            />
                        ))}
                    </Tabs>
                </Box>

                <AnimatePresence mode="wait">
                    <motion.div
                        key={activeTab}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{ duration: 0.4 }}
                    >
                        <Typography
                            sx={{
                                textAlign: 'center',
                                fontSize: { xs: '1rem', md: '1.1rem' },
                                color: '#475569',
                                mb: 5,
                                fontWeight: 500,
                                maxWidth: 700,
                                mx: 'auto',
                            }}
                        >
                            {activeModule.subtitle}
                        </Typography>
                        <Grid container spacing={3}>
                            {activeModule.features.map((feature, index) => (
                                <Grid item xs={12} sm={6} md={4} key={index}>
                                    <motion.div
                                        custom={index}
                                        variants={cardVariants}
                                        initial="hidden"
                                        animate="visible"
                                    >
                                        <Card
                                            onMouseEnter={() => setHoveredCard(index)}
                                            onMouseLeave={() => setHoveredCard(null)}
                                            sx={{
                                                height: '100%',
                                                borderRadius: 2,
                                                border: `1px solid ${alpha(activeModule.color, 0.15)}`,
                                                boxShadow: `0 4px 16px ${alpha(activeModule.color, 0.08)}`,
                                                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                                                cursor: 'pointer',
                                                background: '#fff',
                                                position: 'relative',
                                                overflow: 'hidden',
                                                '&:hover': {
                                                    transform: 'translateY(-8px)',
                                                    boxShadow: `0 20px 40px ${alpha(activeModule.color, 0.12)}`,
                                                    borderColor: alpha(activeModule.color, 0.25),
                                                    '&::before': {
                                                        transform: 'scaleX(1)',
                                                    },
                                                },
                                                '&::before': {
                                                    content: '""',
                                                    position: 'absolute',
                                                    top: 0,
                                                    left: 0,
                                                    right: 0,
                                                    height: 2,
                                                    background: activeModule.color,
                                                    transform: 'scaleX(0)',
                                                    transformOrigin: 'left',
                                                    transition: 'transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                                                },
                                            }}
                                        >
                                            <CardContent
                                                sx={{
                                                    p: 3,
                                                    height: '100%',
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 2,
                                                        mb: 2,
                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            width: 64,
                                                            height: 64,
                                                            borderRadius: 2.5,
                                                            background:
                                                                hoveredCard === index
                                                                    ? alpha(activeModule.color, 0.2)
                                                                    : alpha(activeModule.color, 0.12),
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            flexShrink: 0,
                                                            transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                                                            transform:
                                                                hoveredCard === index ? 'scale(1.08) rotate(-5deg)' : 'scale(1)',
                                                        }}
                                                    >
                                                        <Box
                                                            component={'img'}
                                                            src={feature.image}
                                                            alt={feature.title}
                                                            sx={{
                                                                width: 43,
                                                                height: 43,
                                                                color: activeModule.color,
                                                                transition: 'color 0.4s ease',
                                                            }}
                                                        />
                                                    </Box>

                                                    <Typography
                                                        sx={{
                                                            fontWeight: 800,
                                                            fontSize: '1.1rem',
                                                            color: '#0f172a',
                                                            lineHeight: 1.3,
                                                            letterSpacing: '-0.01em',
                                                            flex: 1,
                                                        }}
                                                    >
                                                        {feature.title}
                                                    </Typography>
                                                </Box>

                                                <Box
                                                    sx={{
                                                        position: 'relative',
                                                        minHeight: 120,
                                                        flex: 1,
                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            position: 'absolute',
                                                            top: 0,
                                                            left: 0,
                                                            right: 0,
                                                            opacity: hoveredCard === index ? 0 : 1,
                                                            transform: hoveredCard === index ? 'translateY(-10px)' : 'translateY(0)',
                                                            transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                                                            pointerEvents: hoveredCard === index ? 'none' : 'auto',
                                                        }}
                                                    >
                                                        <Typography
                                                            sx={{
                                                                fontSize: '0.9rem',
                                                                color: '#64748b',
                                                                lineHeight: 1.6,
                                                            }}
                                                        >
                                                            {feature.desc}
                                                        </Typography>
                                                    </Box>

                                                    <Box
                                                        sx={{
                                                            position: 'absolute',
                                                            top: 0,
                                                            left: 0,
                                                            right: 0,
                                                            opacity: hoveredCard === index ? 1 : 0,
                                                            transform: hoveredCard === index ? 'translateY(0)' : 'translateY(10px)',
                                                            transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                                                            pointerEvents: hoveredCard === index ? 'auto' : 'none',
                                                        }}
                                                    >
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                flexDirection: 'column',
                                                                gap: 1.5,
                                                                pt: 1,
                                                            }}
                                                        >
                                                            {feature.microFeatures.map((micro, i) => (
                                                                <Box
                                                                    key={i}
                                                                    sx={{
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        gap: 1.5,
                                                                        opacity: hoveredCard === index ? 1 : 0,
                                                                        transform: hoveredCard === index ? 'translateX(0)' : 'translateX(-10px)',
                                                                        transition: `all 0.4s cubic-bezier(0.4, 0, 0.2, 1) ${i * 0.05}s`,
                                                                    }}
                                                                >
                                                                    <Box
                                                                        sx={{
                                                                            width: 18,
                                                                            height: 18,
                                                                            borderRadius: '50%',
                                                                            background: alpha(activeModule.color, 0.15),
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            justifyContent: 'center',
                                                                            flexShrink: 0,
                                                                        }}
                                                                    >
                                                                        <Box
                                                                            sx={{
                                                                                width: 8,
                                                                                height: 8,
                                                                                borderRadius: '50%',
                                                                                background: activeModule.color,
                                                                            }}
                                                                        />
                                                                    </Box>
                                                                    <Typography
                                                                        sx={{
                                                                            fontSize: '0.825rem',
                                                                            fontWeight: 600,
                                                                            color: '#475569',
                                                                            lineHeight: 1.4,
                                                                        }}
                                                                    >
                                                                        {micro}
                                                                    </Typography>
                                                                </Box>
                                                            ))}
                                                        </Box>
                                                    </Box>
                                                </Box>
                                            </CardContent>
                                        </Card>
                                    </motion.div>
                                </Grid>
                            ))}
                        </Grid>
                    </motion.div>
                </AnimatePresence>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={isInView ? { opacity: 1, y: 0 } : {}}
                    transition={{ delay: 0.5, duration: 0.6 }}
                >
                    <Box
                        sx={{
                            mt: 6,
                            p: 3,
                            borderRadius: 2,
                            background: '#f8fafc',
                            border: '1px solid #e2e8f0',
                            textAlign: 'center',
                        }}
                    >
                        <Typography
                            sx={{
                                fontSize: '1rem',
                                fontWeight: 600,
                                color: '#0f172a',
                                mb: 0.5,
                            }}
                        >
                            Tüm modüller isteğe bağlıdır.
                        </Typography>
                        <Typography
                            sx={{
                                fontSize: '0.9rem',
                                color: '#64748b',
                                maxWidth: 500,
                                mx: 'auto',
                            }}
                        >
                            Derneğinizin ihtiyacına göre aktif edilir ve özelleştirilir.
                        </Typography>
                    </Box>
                </motion.div>
            </Container>
        </Box>
    );
};

export default ModuleSystemSectionKurumsal;
